import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/AuthContext";
import { adminApi, publicApi } from "../../lib/api";
import { LogOut, Plus, Pencil, Eye, EyeOff, ExternalLink, Loader2, RefreshCw, Users, Settings, FileSpreadsheet, Info, Bot, Calendar, Globe, Database } from "lucide-react";

const StatusBadge = ({ published }) => {
  if (published) {
    return (
      <span className="inline-flex items-center gap-1.5 bg-[#E8F5EC] text-[#0E7C3D] rounded-full px-3 py-1 text-xs">
        <Eye className="w-3 h-3" /> Pubblicata
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 rounded-full px-3 py-1 text-xs">
      <EyeOff className="w-3 h-3" /> Bozza
    </span>
  );
};

const LogoBubble = ({ name, url }) => {
  if (url) return <img src={url} alt="" className="w-9 h-9 rounded-lg object-cover" />;
  return (
    <div className="w-9 h-9 rounded-lg bg-[#F0FAF3] flex items-center justify-center text-[#12964A] font-medium">
      {name?.[0] || "?"}
    </div>
  );
};

const LoadingRow = () => (
  <div className="p-16 flex items-center justify-center text-slate-500">
    <Loader2 className="w-5 h-5 animate-spin" />
  </div>
);

const EmptyRow = () => (
  <div className="p-16 text-center">
    <p className="font-display text-2xl text-[#1F2937]">Nessuna attività ancora inserita.</p>
    <p className="text-slate-500 mt-2">Clicca “Nuova attività” per iniziare a popolare la directory.</p>
  </div>
);

const BusinessTable = ({ rows, onTogglePublish }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="text-left text-xs uppercase tracking-[0.18em] text-slate-500 border-b border-[#E5E7EB]">
        <th className="py-4 px-6">Nome</th>
        <th className="py-4 px-6">Categoria</th>
        <th className="py-4 px-6">Stato</th>
        <th className="py-4 px-6">Aggiornata</th>
        <th className="py-4 px-6 text-right">Azioni</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id} className="border-b border-[#F1F5F9] hover:bg-[#F8F9FA]">
          <td className="py-4 px-6">
            <div className="flex items-center gap-3">
              <LogoBubble name={r.name} url={r.logo_url} />
              <div>
                <div className="font-medium text-[#1F2937]">{r.name}</div>
                <div className="text-xs text-slate-500">{r.slug}</div>
              </div>
            </div>
          </td>
          <td className="py-4 px-6 text-slate-600">{r.category?.name || "—"}</td>
          <td className="py-4 px-6"><StatusBadge published={r.published} /></td>
          <td className="py-4 px-6 text-slate-500">{new Date(r.updated_at || r.created_at).toLocaleString("it-IT")}</td>
          <td className="py-4 px-6">
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => onTogglePublish(r)} className="px-3 py-1.5 rounded-full text-xs border border-[#E5E7EB] hover:border-slate-400">
                {r.published ? "Rimuovi pubbl." : "Pubblica"}
              </button>
              <Link to={`/admin/attivita/${r.id}`} className="inline-flex items-center gap-1.5 bg-[#1F2937] hover:bg-[#12964A] text-white rounded-full px-3 py-1.5 text-xs transition-colors">
                <Pencil className="w-3 h-3" /> Modifica
              </Link>
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const renderBody = ({ loading, rows, onTogglePublish }) => {
  if (loading) return <LoadingRow />;
  if (rows.length === 0) return <EmptyRow />;
  return <BusinessTable rows={rows} onTogglePublish={onTogglePublish} />;
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [b, c] = await Promise.all([
        adminApi.listBusinesses(),
        publicApi.listCategories(),
      ]);
      setRows(b.data || []);
      setCategories(c.data || []);
    } catch (err) {
      console.error("Dashboard load failed:", err);
      setError(err?.response?.data?.detail || "Impossibile caricare i dati.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const togglePublish = useCallback(async (row) => {
    try {
      await adminApi.updateBusiness(row.id, { published: !row.published });
      await load();
    } catch (err) {
      console.error("Toggle publish failed:", err);
      setError(err?.response?.data?.detail || "Errore aggiornamento.");
    }
  }, [load]);

  const doLogout = useCallback(async () => {
    await logout();
    navigate("/admin/login");
  }, [logout, navigate]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#12964A] flex items-center justify-center text-white font-display font-bold">C</div>
            <div>
              <div className="font-display font-bold text-[#1F2937]">CCN Sanluri</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Pannello amministrazione</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-sm text-slate-500">{user?.email}</div>
            <Link to="/" target="_blank" className="text-sm text-slate-600 hover:text-[#12964A] inline-flex items-center gap-1">
              Sito <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <button onClick={doLogout} className="inline-flex items-center gap-2 border border-[#E5E7EB] hover:border-slate-400 rounded-full px-4 py-1.5 text-sm">
              <LogOut className="w-3.5 h-3.5" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Attività</div>
            <h1 className="font-display font-bold text-4xl md:text-5xl text-[#1F2937]">Gestione directory</h1>
            <p className="text-slate-500 mt-2">{rows.length} attività nel database</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/sito" className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm hover:border-slate-400" data-testid="link-site-cms">
              <Globe className="w-4 h-4" /> Sito Web
            </Link>
            <Link to="/admin/richieste" className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm hover:border-slate-400" data-testid="link-membership">
              <Users className="w-4 h-4" /> Richieste
            </Link>
            <Link to="/admin/importazione" className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm hover:border-slate-400" data-testid="link-import">
              <FileSpreadsheet className="w-4 h-4" /> Import/Export
            </Link>
            <Link to="/admin/impostazioni" className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm hover:border-slate-400" data-testid="link-settings">
              <Settings className="w-4 h-4" /> Impostazioni
            </Link>
            <Link to="/admin/informazioni" className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm hover:border-slate-400" data-testid="link-info">
              <Info className="w-4 h-4" /> Info
            </Link>
            <Link to="/admin/chatbot" className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm hover:border-slate-400" data-testid="link-chatbot">
              <Bot className="w-4 h-4" /> Assistente AI
            </Link>
            <Link to="/admin/eventi-v2" className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm hover:border-slate-400" data-testid="link-events">
              <Calendar className="w-4 h-4" /> Eventi
            </Link>
            <Link to="/admin/migrazione-json" className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm hover:border-slate-400" data-testid="link-migration">
              <Database className="w-4 h-4" /> Migrazione JSON
            </Link>
            <button onClick={load} className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-4 py-2.5 text-sm hover:border-slate-400">
              <RefreshCw className="w-4 h-4" /> Aggiorna
            </button>
            <Link to="/admin/attivita/nuova" className="inline-flex items-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] text-white rounded-full px-5 py-2.5 text-sm transition-colors">
              <Plus className="w-4 h-4" /> Nuova attività
            </Link>
          </div>
        </div>

        {error && <div className="mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}

        <div className="bg-white rounded-3xl border border-[#E5E7EB] overflow-hidden">
          {renderBody({ loading, rows, onTogglePublish: togglePublish })}
        </div>

        {categories.length > 0 && (
          <div className="mt-10 text-sm text-slate-500">
            Categorie disponibili: {categories.map((c) => c.name).join(", ")}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
